import { ModalPanel } from '@/contasis';

interface ShortcutEntry {
  keys: string;
  description: string;
}

interface ShortcutGroup {
  title: string;
  entries: ShortcutEntry[];
}

const GROUPS: ShortcutGroup[] = [
  {
    title: 'Global (en cualquier pantalla)',
    entries: [
      { keys: 'Ctrl + K', description: 'Abrir la paleta de comandos' },
      { keys: 'Ctrl + Shift + F', description: 'Nueva factura' },
      { keys: 'Ctrl + Shift + B', description: 'Nueva boleta' },
      { keys: 'Ctrl + Shift + C', description: 'Nuevo cliente' },
      { keys: 'Ctrl + Shift + P', description: 'Nuevo producto' },
      { keys: 'Ctrl + 1 … Ctrl + 6', description: 'Ir a Comprobantes, Productos, Clientes, Cajas, Cobranzas o Indicadores' },
    ],
  },
  {
    title: 'Comprobantes, Documentos Comerciales y Compras',
    entries: [
      { keys: 'F2', description: 'Enfocar el buscador de productos' },
      { keys: 'F3', description: 'Enfocar el precio unitario de la fila actual (o la última agregada)' },
      { keys: 'Ctrl + Enter', description: 'Confirmar / emitir / registrar (acción principal)' },
      { keys: 'Ctrl + S', description: 'Guardar como borrador' },
      { keys: 'Ctrl + P', description: 'Vista previa del documento' },
      { keys: 'Esc', description: 'Cancelar y volver al listado' },
      { keys: '↑ / ↓', description: 'Navegar los resultados del buscador de cliente o producto' },
      { keys: 'Enter', description: 'Seleccionar el resultado resaltado del buscador' },
      { keys: '↑ / ↓ / ← / →', description: 'Moverse entre celdas de la tabla de productos' },
      { keys: '↑ / ↓', description: 'Aumentar / disminuir la cantidad con los botones +/- de la fila' },
      { keys: 'Tab / Shift + Tab', description: 'Avanzar / retroceder entre campos del formulario' },
    ],
  },
  {
    title: 'Punto de Venta (POS)',
    entries: [
      { keys: 'F2', description: 'Enfocar el buscador de productos' },
      { keys: 'F3', description: 'Enfocar la búsqueda de cliente' },
      { keys: 'F4', description: 'Cobrar / emitir (igual que Ctrl + Enter)' },
      { keys: 'F5', description: 'Refrescar productos' },
      { keys: 'F6', description: 'Abrir caja (si está cerrada)' },
      { keys: 'F7', description: 'Imprimir comanda para cocina (mesa activa)' },
      { keys: 'F8', description: 'Activar / desactivar cobranza automática' },
      { keys: 'F9', description: 'Quitar todos los productos del carrito' },
      { keys: 'F10', description: 'Guardar y volver a mesas (mesa activa)' },
      { keys: 'Ctrl + M', description: 'Ver plano de mesas' },
      { keys: 'Ctrl + D', description: 'Aplicar descuento' },
      { keys: 'Ctrl + ,', description: 'Abrir configuración del POS' },
      { keys: '↑ / ↓', description: 'Navegar los resultados de búsqueda de productos o clientes' },
      { keys: 'Enter', description: 'Agregar al carrito el producto resaltado' },
      { keys: '↑ / ↓', description: 'Aumentar / disminuir la cantidad del ítem enfocado en el carrito' },
      { keys: 'Ctrl + Enter', description: 'Ir a cobrar (o cobrar automáticamente si hay auto-cobranza activa)' },
      { keys: 'Ctrl + P', description: 'Imprimir precuenta' },
    ],
  },
  {
    title: 'Plano de Mesas',
    entries: [
      { keys: 'Ctrl + N', description: 'Nueva mesa' },
      { keys: 'Ctrl + G', description: 'Gestión de áreas' },
      { keys: 'F5', description: 'Refrescar mesas' },
      { keys: 'Esc', description: 'Volver a Punto de Venta' },
      { keys: 'F6', description: 'Iniciar limpieza (en el modal de limpieza)' },
      { keys: 'F7', description: 'Finalizar limpieza (en el modal de limpieza)' },
    ],
  },
  {
    title: 'Modales y ventanas emergentes',
    entries: [
      { keys: 'Esc', description: 'Cerrar la ventana o cancelar la acción' },
      { keys: 'Tab / Shift + Tab', description: 'Moverse entre los campos y botones del modal' },
      { keys: 'Enter', description: 'Confirmar la acción principal (venta exitosa, opciones post-emisión)' },
      { keys: 'Ctrl + Enter', description: 'Confirmar el cobro en el modal de cobranza' },
    ],
  },
];

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ShortcutKeys = ({ keys }: { keys: string }) => (
  <kbd className="px-2 py-1 rounded-md border border-default bg-surface-1 font-mono text-[12px] leading-none text-secondary whitespace-nowrap">
    {keys}
  </kbd>
);

export const KeyboardShortcutsModal = ({ isOpen, onClose }: KeyboardShortcutsModalProps) => (
  <ModalPanel
    isOpen={isOpen}
    onClose={onClose}
    title="Atajos de teclado"
    subtitle="Usa Senciyo casi sin mouse en Comprobantes, Documentos Comerciales, Compras y Punto de Venta"
    size="medium"
  >
    <div className="px-6 py-4 space-y-6">
      {GROUPS.map((group) => (
        <div key={group.title}>
          <h3 className="text-ui-sm font-semibold text-primary mb-2">{group.title}</h3>
          <div className="rounded-lg border border-default divide-y divide-default overflow-hidden">
            {group.entries.map((entry) => (
              <div
                key={`${group.title}-${entry.description}`}
                className="flex items-center justify-between gap-4 px-4 py-2.5 bg-surface-0"
              >
                <span className="text-sm text-secondary">{entry.description}</span>
                <ShortcutKeys keys={entry.keys} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </ModalPanel>
);

export default KeyboardShortcutsModal;
